import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Form, Row, Alert } from 'react-bootstrap';
import Select, { MultiValue } from 'react-select';
import createAxiosInstance from '../../../services/axiosConfig';
import Spinner from '../../../shared/spinner';

const { REACT_APP_ESTEIRA_BACK } = process.env;

// Instâncias Axios
const feriadosAxios = createAxiosInstance(REACT_APP_ESTEIRA_BACK + '/rest/feriados');
const empregadoresAxios = createAxiosInstance(REACT_APP_ESTEIRA_BACK + '/rest/empregadorController');

// --- Tipos ---
interface FeriadoDetalhado {
  id: number;
  data: string;
  descricao: string;
  tipo: string;
  empregadorIds: number[];
}

interface Empregador {
  id: number;
  nome: string;
}

interface SelectOption {
  value: number;
  label: string;
}

const FeriadosEdicaoComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState('Nacional');
  const [empregadores, setEmpregadores] = useState<SelectOption[]>([]);
  const [selectedEmpregadores, setSelectedEmpregadores] = useState<MultiValue<SelectOption>>([]);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  
  useEffect(() => {
    const carregarDadosParaEdicao = async () => {
      try {
        const [resFeriado, resEmpregadores] = await Promise.all([
          feriadosAxios.get<FeriadoDetalhado>(`/getFeriadoById/${id}`),
          empregadoresAxios.get<Empregador[]>('/listarTodosEmpregadoresAtivos')
        ]);

        const feriadoData = resFeriado.data;
        const todosEmpregadores = resEmpregadores.data;

        setData(feriadoData.data);
        setDescricao(feriadoData.descricao);
        setTipo(feriadoData.tipo);

        const empregadorOptions = todosEmpregadores.map(emp => ({
          value: emp.id,
          label: emp.nome,
        }));
        setEmpregadores(empregadorOptions);

        if (feriadoData.tipo === 'Regional' && feriadoData.empregadorIds) {
          const empregadoresJaVinculados = empregadorOptions.filter(option =>
            feriadoData.empregadorIds.includes(option.value)
          );
          setSelectedEmpregadores(empregadoresJaVinculados);
        }

      } catch (err) {
        console.error("Erro ao carregar dados para edição:", err);
        setErro('Erro ao carregar dados do feriado.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      carregarDadosParaEdicao();
    }
  }, [id]);

  const handleAtualizarFeriado = async () => {
    setErro('');

    if (!data || !descricao || !tipo) {
      setErro('Preencha todos os campos obrigatórios.');
      return;
    }
    if (tipo === 'Regional' && selectedEmpregadores.length === 0) {
        setErro('Para feriados regionais, selecione ao menos um empregador.');
        return;
    }

    const payload = {
      data,
      descricao,
      tipo,
      empregadorIds: tipo === 'Regional' ? selectedEmpregadores.map(opt => opt.value) : []
    };

    try {
      await feriadosAxios.put(`/altFeriados/${id}`, payload);
      navigate('/parametros-feriados', { state: { sucesso: 'Feriado atualizado com sucesso!' } });
    } catch (err: any) {
      console.error(err);
      setErro(err.response?.data?.message || 'Erro ao atualizar feriado.');
    }
  };

  if (loading) {
    return <Spinner loading={loading} />;
  }

  return (
    <div className="position-relative m-md-3">
      <h5>Parâmetros Cartão &gt; Editar Feriado</h5>

      <Row className="mb-3 mt-4">
        <Form.Group as={Col} md="3">
          <Form.Label>Data</Form.Label>
          <Form.Control type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </Form.Group>
        <Form.Group as={Col} md="4">
          <Form.Label>Nome do Feriado</Form.Label>
          <Form.Control type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </Form.Group>
        <Form.Group as={Col} md="3">
          <Form.Label>Tipo de feriado</Form.Label>
          <Form.Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="Nacional">Nacional</option>
            <option value="Regional">Regional</option>
          </Form.Select>
        </Form.Group>
      </Row>

      {tipo === 'Regional' && (
        <Row className="mb-3">
          <Form.Group as={Col} md="7">
            <Form.Label>Empregadores Vinculados</Form.Label>
            <Select
              isMulti
              options={empregadores}
              value={selectedEmpregadores}
              onChange={(options) => setSelectedEmpregadores(options)}
              placeholder="Selecione um ou mais empregadores..."
              noOptionsMessage={() => 'Nenhum empregador encontrado'}
            />
          </Form.Group>
        </Row>
      )}

      <Row className="mb-4">
        <Col md="3">
          <Button onClick={handleAtualizarFeriado} id="App-button-cor-principal-escura">
            Salvar Alterações
          </Button>
        </Col>
        <Col md="3">
          <Button variant="secondary" onClick={() => navigate('/parametros-feriados')}>
            Voltar
          </Button>
        </Col>
      </Row>

      {erro && <Alert variant="danger">{erro}</Alert>}
    </div>
  );
};

export default FeriadosEdicaoComponent;
